<template>
  <div
    v-if="selectedFunctionalUnit"
    class="mt-3 mb-5" >
    <strong>Functional Unit: </strong>
    {{ selectedFunctionalUnit }}
  </div>
  <div class="form-step">
    <v-row>
      <v-col cols="4">
        <v-label class="mb-3">Select a KPI Name:</v-label>
        <v-form fast-fail label="Name">
          <v-autocomplete
            label="KPI name"
            :items="kpiTypes"
            item-title="text"
            item-value="value"
            variant="solo"
            item-disabled="disable"
            v-model="selectedKpi"
            hint="Select one of the available KPIs"
            persistent-hint
            class="mb-6"
            :rules="rules"
            @update:modelValue="onChangeMutli"
            :disabled="isPersisted"
          ></v-autocomplete>
        </v-form>

      </v-col>
    </v-row>
    <v-form fast-fail>
    <v-row class="mt-2">
      <v-col cols="4">
        <div class="multiselect-dropdown">
          <v-label v-if="selectedKpi">Select aggregation level(s)</v-label>
          <v-chip-group
              v-if="selectedKpi"
              column
              v-model="selectedAggregation"
              :rules="FunctionalUnitRules"
              @update:modelValue="aggregationLevelHandler"
              multiple>
            <v-chip v-for="item in optionsAggregation"
                :text="item.text"
                :value="item"
                :elevation="2"
                :color="selectClassCoding(item.text)"
                variant="outlined"
                filter
                />
          </v-chip-group>
        </div>
      </v-col>
      <v-col cols="12">
        <v-row v-if="form.kpi.likert === 'true'">
          <v-col>
              <v-checkbox
                  id="kpi_likert"
                  label="Likert scale will be used"
                  v-model="form.kpi.likert"
                  name="kpi_likert"
                  value="true"
                  unchecked-value="false"
                  disabled>
              </v-checkbox>
          </v-col>
        </v-row>
        <v-row class="" v-if="selectedAggregation.length > 0">
          <v-col class="label">
            Enter value for each aggregation level
            <v-tooltip>
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  size="x-small"
                  icon
                  variant="outlined"
                  color="#0d47a1"
                >
                  <v-icon icon="fas fa-info" />
                </v-btn>
              </template>
              <div style="max-width: 200px; white-space: normal">
                Enter a real value for each aggregation level. If the functional
                unit is Likert Scale then the value should be in range [1, 5].
              </div>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="3"
            v-for="(level, index) in selectedAggregation"
            :key="level.text">
            <span>
              <strong>{{ level.text }}</strong>
            </span>
            <v-text-field
              class="mt-2"
              :id="`${level.value}_value`"
              v-model="form.aggregationLevels[index].value"
              :bg-color="fieldColorCoding(level.value)"
              label="Enter value"
              :min="minValue"
              :max="maxValue"
              variant="solo"
              required
              :rules="textFieldrules"
            >
            </v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-form>
  </div>
</template>

<script setup>
import { fieldColorCoding } from "@/Shared/StepsRules.vue";
import {computed, ref, watch} from "vue";

const componentKey = ref(0);
const emit = defineEmits(["name-changed"]);

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  kpiTypes: {
    type: Array,
    required: true,
  },
});

const rules = [
  (value) => {
    if (value) return true;
    return "You must select a KPI.";
  },
];

const FunctionalUnitRules = [
  (value) => {
    if (value.length !== 0) return true;
    return "You must select an Aggregation Level.";
  },
];

const textFieldrules = [
  (value) => {
    if(selectedFunctionalUnit.value.includes('Likert') === true){
      if (value >= 1 && value <= 5) return true;
      return "You must enter a number between 1 and 5.";
    }
    else{
      if (value) return true;
      return "You must enter a value.";
    }
  },
];

const form = props.modelValue;
const validation = computed(() => {
  return form.kpi.name !== null;
});

const selectedFunctionalUnit = computed(() => {
  const obj = props.kpiTypes.find((type) => type.kpi_type_id === form.kpi.kpi_type_id);
  if (obj !== undefined) return obj.functionalUnit;
  return "";
});

/**
 * Watches changes in functional unit and sets the likert attribute
 * value to the expected one.
 */
watch(selectedFunctionalUnit, async (newValue, _) => {
  form.kpi.likert = newValue.toLowerCase().includes('likert')
})

const minValue = computed(() => {
  return 0;
});

const maxValue = computed(() => {
  if (form.kpi.likert === "true") return 5;
  else return 1000;
});

const selectedKpi = computed({
  get() {
    if (form.kpi.kpi_type_id) {
      return props.kpiTypes.filter((e) => e.kpi_type_id === form.kpi.kpi_type_id)[0];
    }
    else return "";
  },
  set(name) {
    return name;
  },
});

/**
 * Checks if the KPI created/edited has already been saved
 * in the database. In essence, it decides the "mode" of
 * the form, whether is "new" or "edit".
 * @return [Boolean]
 * @type {ComputedRef<boolean>}
 */
const isPersisted = computed(() => {
  return form.kpi.hasOwnProperty('created_at')
})

function selectClassCoding(field) {
  const colorCode = {
    Building: 'red-darken-3',
    PED: 'green-darken-3',
    PEB: 'blue-darken-3',
    City: 'yellow-darken-4',
    Island: 'purple-darken-3'
  }
  return colorCode[field];
}

const selectedAggregation = computed({
  get() {
    let arr = [];
    for (let i = 0; i < form.aggregationLevels.length; i++)
      arr.push({
        value: form.aggregationLevels[i].name,
        text: form.aggregationLevels[i].text,
      });
    return arr;
  },
  set(val) {},
});

//---------------------Aggregation Level-----------------------------------------------
const optionsAggregation = [
  { value: "Building", text: "Building" },
  { value: "PEB", text: "PEB" },
  { value: "PED", text: "PED" },
  { value: "City", text: "City" },
  { value: "Island", text: "Island" }
];

function aggregationLevelHandler(level) {
  const formItems = form.aggregationLevels;
  if (level.length > form.aggregationLevels.length) {
    let it = level.filter(
      (items) => !formItems.some((items2) => items2.name === items.value)
    );
    addAggregationLevel(it, formItems);
  } else {
    let it = formItems.filter(
      (items) => !level.some((items2) => items2.value === items.name)
    );
    removeAggregationLevel(it, formItems);
  }
}

function addAggregationLevel(level, formItems) {
  // adding dimension weight from this step, because I had some problems in
  // the second step. However, try to find a way to add it in the second step.
  formItems.push({
    name: level[0].value,
    text: level[0].text,
    value: "",
    dimension_weight: "",
  });
}

function removeAggregationLevel(level, formItems) {
  const index = formItems.findIndex((prop) => prop.name === level[0].name);
  formItems.splice(index, 1);

  // TODO: [refactor] Maybe move these lines in a more related file
  // margins for those levels should also be removed
  if (form.kpi.likert === "false") {
    const kpiMargins = form.kpi.kpi_margins_attributes;
    const marginIndex = kpiMargins.findIndex(
      (item) => item.level === level.value
    );
    if (marginIndex > -1) {
      const marginDbId = kpiMargins[marginIndex].id;
      kpiMargins.splice(marginIndex, 1);
      // if in edit mode an extra item should be pushed to destroy the element in the db
      kpiMargins.push({ id: marginDbId, _destroy: 1 });
    }
  }
}

function checkIfLikert() {
  if (selectedFunctionalUnit.value.startsWith("Likert Scale (1-5)")) form.kpi.likert = "true";
  else form.kpi.likert = "false";
}

function onChangeMutli(selected, id) {
  const selectedType = props.kpiTypes.find((type) => type.text === selected);
  if (selectedType) {
    form.kpi.kpi_type_id = selectedType.kpi_type_id;
    form.kpi.name = selectedType.text
    checkIfLikert();
  }
  emit("name-changed", selectedType.text);
}

function onValueChange($event, index) {
  const strValue = form.aggregationLevels[index].value.toString();
  if (form.kpi.likert === "true" && strValue.includes(".")) {
    form.aggregationLevels[index].value = parseInt(strValue);
  }
}
</script>

<style scoped>
strong {
  font-size: medium;
}
.label {
  font-size: medium;
}
</style>
